import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, debounce, filter, map, switchMap } from 'rxjs/operators';
import { CategoryService } from '@emi/shared/data-access';
import * as categoryActions from './category-action';
import { HttpErrorResponse } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { CategoryState } from './category-reducer';
import { getCategoryState } from './category-selectors';
import { Category, CategoryDto, Filters } from '@emi/shared/models';
import { getSelectedFilters } from '../extraction/extraction-selectors';
import { DataExtractionState } from '../extraction/extraction-reducer';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';
import { createFilterDto } from '@emi/shared/utils';

@Injectable()
export class CategoryEffects {
    private currentState: CategoryState;
    private currentFilters: Filters;
    private previousFilters: Filters;
    private searchTermState: string;
    private previousSearchTermState: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private categoryService: CategoryService,
        private categoryStore: Store<CategoryState>,
        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>
    ) {
        this.categoryStore.pipe(select(getCategoryState)).subscribe((x) => (this.currentState = x));
        this.skuSearchStore.pipe(select(selectSkuSearch)).subscribe((skuSearchState) => (this.searchTermState = skuSearchState.searchTerm, this.includeSearchPhrase = skuSearchState.includeSearchPhrase));
    }

    getCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(categoryActions.getCategories),
            map(() => {
                return categoryActions.isLoadingCategories();
            })
        )
    );

    fetchCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(categoryActions.fetchCategories),
            switchMap(() => {
                this.store.pipe(select(getSelectedFilters)).subscribe((filtersState) => (this.currentFilters = filtersState));
                if (this.currentFilters.categories.length > 0) {
                    if (this.currentFilters === this.previousFilters && this.searchTermState === this.previousSearchTermState) {
                        return of(
                            categoryActions.getCategoriesSuccess({
                                categories: this.currentState.categories,
                            })
                        );
                    }
                }

                return this.categoryService
                    .getCategories(createFilterDto({ filterState: this.currentFilters,                         
                        searchTerm: this.includeSearchPhrase?this.searchTermState : undefined }))
                    .pipe(
                        map((data: CategoryDto) => {
                            this.previousFilters = this.currentFilters;
                            this.previousSearchTermState = this.searchTermState;
                            return categoryActions.getCategoriesSuccess({
                                categories: data.results,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(categoryActions.getCategoriesFailure({ error }));
                        })
                    );
            })
        )
    );

    searchCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(categoryActions.searchCategories),
            debounce((action) => timer(action.delay)),
            filter((action) => action.searchText !== this.currentState.searchText),
            map((action) => {
                if (action.searchText !== '') {
                    const searchedCategories = this.getSearchedCategories(this.currentState.categories, action.searchText) ?? [];
                    return categoryActions.searchCategoriesSuccess({ categories: searchedCategories, searchText: action.searchText });
                } else {
                    return categoryActions.searchCategoriesSuccess({ categories: [], searchText: null });
                }
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(categoryActions.isLoadingCategories),
            map(() => {
                return categoryActions.fetchCategories();
            })
        )
    );

    private getSearchedCategories(categories: Category[], searchText: string): Category[] | null {
        const relevantCategories = [];
        for (const category of categories) {
            if (category.name.toLowerCase().includes(searchText.toLowerCase())) {
                relevantCategories.push(category);
            } else if (category.childCategories) {
                const relevantChildren = this.getSearchedCategories(category.childCategories, searchText);
                if (relevantChildren) {
                    let newCategory = { ...category, childCategories: relevantChildren };
                    relevantCategories.push(newCategory);
                }
            }
        }
        return relevantCategories.length > 0 ? relevantCategories : null;
    }
}
